import React from "react";
import NextLink from "next/link";
import { Box, Typography } from "@mui/material";
import ArticleAuthorsList from "@/src/components/journal-site/common/article/article-authors-list/article-authors-list";
import DownloadBox from "@/src/components/journal-site/common/article/mui-custom/article-information/download-box";
import Kicker from "@/src/components/common/broadsheet/kicker";
import { TITLE_ALLOWED_TAGS } from "@/src/constant/constant";
import ArticleList from "@/src/_types/Article/article-list";
import { stripHTMLTags } from "@/src/utils/strip-html-tags";
import ReactHtmlParser from "@/src/utils/html-parser";
import {
  FONT_DISPLAY,
  FONT_TEXT,
  FONT_UI,
} from "@/src/components/common/broadsheet/tokens";

interface ArticleProps {
  article: ArticleList;
  number: number;
}

const InPressArticle: React.FC<ArticleProps> = ({ article, number }) => (
  <Box sx={{ display: "flex", gap: 0.625, minWidth: 0 }}>
    <Box
      aria-hidden="true"
      sx={{
        flex: "none",
        width: "1.75rem",
        fontFamily: FONT_DISPLAY,
        fontWeight: 700,
        fontSize: "1.5rem",
        lineHeight: 1.1,
        color: "custom.ruleHairline",
      }}
    >
      {number}
    </Box>

    <Box sx={{ minWidth: 0 }}>
      {article.document_type ? (
        <Kicker component="p" sx={{ mb: 0.125 }}>
          {article.document_type}
        </Kicker>
      ) : null}

      <Typography
        component="h3"
        sx={{
          m: 0,
          fontFamily: FONT_TEXT,
          fontWeight: 700,
          fontSize: "1rem",
          lineHeight: 1.25,
          "& a": {
            color: "custom.link",
            textDecoration: "none",
            "&:hover": {
              color: "custom.linkHover",
              textDecoration: "underline",
              textUnderlineOffset: "0.15em",
            },
          },
        }}
      >
        <NextLink href={article.href}>
          {ReactHtmlParser(stripHTMLTags(article.title, TITLE_ALLOWED_TAGS))}
        </NextLink>
      </Typography>

      <Box
        sx={{
          mt: 0.125,
          fontFamily: FONT_TEXT,
          fontSize: "0.8125rem",
          lineHeight: 1.5,
          color: "custom.inkMuted",
        }}
      >
        <ArticleAuthorsList authors={article.authors} />
      </Box>

      <Box
        sx={{
          mt: 0.25,
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 0.375,
          fontFamily: FONT_UI,
          fontSize: "0.71875rem",
          color: "custom.subtitleText",
        }}
      >
        {article.pdf_file ? <DownloadBox href={article.pdf_file} /> : null}
        {article.desired_issue &&
        article.issue_id !== article.desired_issue.id ? (
          <Box component="span">
            {article.proof_status} — scheduled for{" "}
            {article.desired_issue.volume_name} ({article.desired_issue.name})
          </Box>
        ) : null}
      </Box>
    </Box>
  </Box>
);

export default InPressArticle;
